import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import {
  HeadlineL,
  HeadlineS,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { DumbPlayerAppearance } from '@sorare/core/src/components/scoring/DumbPlayerAppearance';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { cardAttributes, fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { ScoresCard_averageLastScore } from './__generated__/index.graphql.ts';

const Wrapper = styled(Horizontal).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  border-radius: var(--double-unit);
  flex-wrap: wrap;
  align-items: flex-start;

  @media ${desktopAndAbove} {
    justify-content: space-between;
  }
`;
const Item = styled(Vertical)`
  width: calc(25% - var(--double-unit));
  min-width: 100px;
  align-items: flex-start;
`;

type Props = {
  sport?: Sport;
  averageLastScore: Nullable<ScoresCard_averageLastScore>;
  mode: TotalAveragePlayerScore;
};

export const ScoresCard = ({ sport, averageLastScore, mode }: Props) => {
  const { formatNumber } = useIntlContext();
  if (
    !averageLastScore ||
    !sport ||
    !isType(averageLastScore, 'AverageLastScore')
  )
    return null;
  const {
    averageValueTotal,
    averageValueAllAround,
    averageValueDecisive,
    percentageOfGamesPlayed,
  } = averageLastScore;

  return (
    <Wrapper>
      <Item>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...cardAttributes.averageScore} />
        </LabelM>
        <HeadlineL
          className="text-gradient-color"
          style={
            {
              '--text-gradient-color': findThresholdColor(
                averageValueTotal || 0,
                sport,
                'PLAYER_SCORE'
              ).color,
            } as CSSProperties
          }
        >
          {averageValueTotal}
        </HeadlineL>
      </Item>
      {mode !== AveragePlayerScore.SEASON_AVERAGE_SCORE && (
        <Item>
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage defaultMessage="Played" />
          </LabelM>
          <DumbPlayerAppearance
            mode={mode}
            size="medium"
            appearances={percentageOfGamesPlayed / 100}
            withTooltip
          />
        </Item>
      )}
      <Item>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...fantasy.allAround} />
        </LabelM>
        <Tooltip
          title={
            <FormattedMessage defaultMessage="All-Around Score (AA) totals the statistics during a match that are less obvious to follow but are valuable for evaluating a player's overall performance and his impact on a match" />
          }
        >
          <HeadlineS color="var(--c-nd-600)">
            {formatNumber(averageValueAllAround, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}
          </HeadlineS>
        </Tooltip>
      </Item>
      <Item>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...fantasy.decisive} />
        </LabelM>
        <Tooltip
          title={
            <FormattedMessage defaultMessage="Decisive Score (DS) totals the statistics that have a direct impact on a match (i.e. goal, decisive pass, red card)" />
          }
        >
          <HeadlineS color="var(--c-nd-600)">
            {formatNumber(averageValueDecisive, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}
          </HeadlineS>
        </Tooltip>
      </Item>
    </Wrapper>
  );
};

ScoresCard.fragments = {
  averageLastScore: gql`
    fragment ScoresCard_averageLastScore on AverageLastScoreInterface {
      id
      ... on AverageLastScore {
        id
        averageValueAllAround
        averageValueDecisive
        averageValueTotal
        percentageOfGamesPlayed
      }
    }
  ` as TypedDocumentNode<ScoresCard_averageLastScore>,
};
